import React, { useEffect, useState } from 'react'

import { Box, Button, Icon, Modal, Text } from '@fairfashion/design-system'

import {
  InvoiceIAParserFileStatusDTO,
  InvoiceParserResponseItems,
} from '../../../sdk/invoices/invoice.types'
import * as Styles from '../ia-result.styles'

interface InvoiceItemsModalProps {
  open: boolean
  onClose: () => void
  invoice: InvoiceIAParserFileStatusDTO | null
  fileName: string
  editingData: Record<string, any>
  handleEditData: (invoiceCode: string, field: string, value: any) => void
  onItemsChange: (items: InvoiceParserResponseItems[]) => void
}

export const InvoiceItemsModal = ({
  open,
  onClose,
  invoice: result,
  fileName,
  editingData,
  handleEditData,
  onItemsChange,
}: InvoiceItemsModalProps) => {
  const invoiceCode = getInputValue(
    editingData,
    `${fileName}_code`,
    result?.data.code || '',
  )

  const [localItems, setLocalItems] = useState(() =>
    result?.data?.items ? [...result.data.items] : [],
  )

  useEffect(() => {
    setLocalItems(result?.data?.items ? [...result.data.items] : [])
  }, [result])

  useEffect(() => {
    onItemsChange(localItems)
  }, [localItems, onItemsChange])

  const handleAddItem = () => {
    setLocalItems((prev) => [
      ...prev,
      { code: '', description: '', quantity: 0, taxUnit: '' },
    ])
    const newIndex = localItems.length
    handleEditData(fileName, `item_${newIndex}_code`, '')
    handleEditData(fileName, `item_${newIndex}_description`, '')
    handleEditData(fileName, `item_${newIndex}_quantity`, '')
    handleEditData(fileName, `item_${newIndex}_taxUnit`, '')
  }

  const handleRemoveItem = (index: number) => {
    setLocalItems((prev) => prev.filter((_, i) => i !== index))
  }

  function getInputValue(
    editingData: Record<string, any>,
    key: string,
    fallbackValue: any,
  ) {
    return key in editingData ? editingData[key] : fallbackValue
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Itens da NF ${invoiceCode}`}
      width="900px"
      maxWidth="95%"
    >
      {result && (
        <Box direction="column" gap={3} paddingTop={2} width="100%">
          <Box direction="column" gap={2}>
            <Text size="small" fontWeight={500}>
              Informações da Nota Fiscal
            </Text>
            <Box direction="row" gap={2} flexWrap="wrap">
              <Box direction="column" gap={1} minWidth="150px">
                <Text size="small" color="grey_3">
                  Código
                </Text>
                <Text size="small">{result.data?.code || ''}</Text>
              </Box>
              <Box direction="column" gap={1} minWidth="200px">
                <Text size="small" color="grey_3">
                  Fornecedor
                </Text>
                <Text size="small">{result.data?.providerName || ''}</Text>
              </Box>
              <Box direction="column" gap={1} minWidth="200px">
                <Text size="small" color="grey_3">
                  Cliente
                </Text>
                <Text size="small">{result.data?.clientName || ''}</Text>
              </Box>
            </Box>
          </Box>

          <Box direction="column" gap={2}>
            <Box
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Button variant="outlined" onClick={handleAddItem}>
                <Icon name="add" size={16} color="primary" /> Adicionar item
              </Button>
            </Box>
            <Box
              direction="column"
              gap={2}
              maxHeight="400px"
              overflow="auto"
              padding={2}
              backgroundColor="grey_6"
              borderRadius="8px"
            >
              {localItems.length === 0 && (
                <Box
                  height="100px"
                  width="100%"
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                  gap={1}
                >
                  <Icon name="file" size={32} color="grey_3" />
                  <Text color="grey_3" size="small">
                    Nenhum item encontrado
                  </Text>
                </Box>
              )}
              {localItems.map((item, itemIndex) => (
                <Box
                  key={itemIndex}
                  direction="row"
                  gap={2}
                  padding={2}
                  backgroundColor="white"
                  borderRadius="4px"
                  alignItems="center"
                >
                  <Box direction="column" minWidth="120px">
                    <Styles.RequiredInput
                      label="Código"
                      placeholder=" "
                      value={getInputValue(
                        editingData,
                        `${fileName}_item_${itemIndex}_code`,
                        item.code,
                      )}
                      isEmpty={
                        !getInputValue(
                          editingData,
                          `${fileName}_item_${itemIndex}_code`,
                          item.code,
                        )
                      }
                      onChange={(e) =>
                        handleEditData(
                          fileName,
                          `item_${itemIndex}_code`,
                          e.target.value,
                        )
                      }
                    />
                  </Box>
                  <Box direction="column" minWidth="200px">
                    <Styles.RequiredInput
                      label="Descrição"
                      placeholder=" "
                      value={getInputValue(
                        editingData,
                        `${fileName}_item_${itemIndex}_description`,
                        item.description,
                      )}
                      isEmpty={
                        !getInputValue(
                          editingData,
                          `${fileName}_item_${itemIndex}_description`,
                          item.description,
                        )
                      }
                      onChange={(e) =>
                        handleEditData(
                          fileName,
                          `item_${itemIndex}_description`,
                          e.target.value,
                        )
                      }
                    />
                  </Box>
                  <Box direction="column" minWidth="100px">
                    <Styles.RequiredInput
                      label="Quantidade"
                      placeholder=" "
                      type="number"
                      value={getInputValue(
                        editingData,
                        `${fileName}_item_${itemIndex}_quantity`,
                        Number(item.quantity),
                      )}
                      isEmpty={
                        !getInputValue(
                          editingData,
                          `${fileName}_item_${itemIndex}_quantity`,
                          Number(item.quantity),
                        )
                      }
                      onChange={(e) =>
                        handleEditData(
                          fileName,
                          `item_${itemIndex}_quantity`,
                          e.target.value,
                        )
                      }
                    />
                  </Box>
                  <Box direction="column" minWidth="100px">
                    <Styles.RequiredInput
                      label="Tipo de unidade"
                      placeholder=" "
                      value={getInputValue(
                        editingData,
                        `${fileName}_item_${itemIndex}_taxUnit`,
                        item.taxUnit,
                      )}
                      isEmpty={
                        !getInputValue(
                          editingData,
                          `${fileName}_item_${itemIndex}_taxUnit`,
                          item.taxUnit,
                        )
                      }
                      onChange={(e) =>
                        handleEditData(
                          fileName,
                          `item_${itemIndex}_taxUnit`,
                          e.target.value,
                        )
                      }
                    />
                  </Box>
                  <Box
                    direction="column"
                    minWidth="100px"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleRemoveItem(itemIndex)}
                      title="Remover item"
                    >
                      <Icon name="close" size={16} color="error" />
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box gap={2} justifyContent="flex-end" width="100%">
            <Button onClick={onClose} variant="outlined">
              Fechar
            </Button>
          </Box>
        </Box>
      )}
    </Modal>
  )
}
